import {Injectable, OnDestroy} from '@angular/core';
import {catchError, filter, map, shareReplay} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {forkJoin, Observable, of, Subscription, BehaviorSubject} from 'rxjs';
import {NavigationConfigModel} from 'src/models/navigation/navigation-config.model';
import {Breadcrumb} from 'src/models/navigation/breadcrumb.model';
import {SalesChannel, SalesChannelNavigationPath} from 'src/models/orders/sales.channel.enum';
import {OrderStatus, OrderStatusNavigationPath} from 'src/models/orders/orderStatus';
import {OrderFacade} from 'src/services/orders/order.facade';
import {ImportConfigFacade} from 'src/services/import-configs/import-config.facade';
import {SalesChannelWithWorkflowStatus} from 'src/models/orders/salesChannelWithWorkflowStatus';
import {NavigationPath} from 'src/models/navigation/navigation.path.enum';
import {InfoMessageService} from 'src/services/info-messages/info-message.service';
import {InfoMessageSeverity} from '@shared/enums/infoMessageSeverity';
import {RoleService} from 'src/services/auth/role.service';
import {Roles} from '@shared/enums/roles.enum';

const ORDER_ROLES: string[] = [Roles.Admin, Roles.User, Roles.QWiz, Roles.Hedgehog];
const ADMIN_ROLES: string[] = [Roles.Admin, Roles.QWiz, Roles.Hedgehog];

@Injectable({
	providedIn: 'root'
})
export class NavigationService implements OnDestroy {
	private navConfig!: NavigationConfigModel[];

 private activeSubNavigation: BehaviorSubject<NavigationConfigModel[]> = new BehaviorSubject<NavigationConfigModel[]>([]);
	private mainNavigation: BehaviorSubject<NavigationConfigModel[]> = new BehaviorSubject<NavigationConfigModel[]>([]);
	private breadcrumbs: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject<Breadcrumb[]>([]);
	private customBreadcrumbTitle: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

	private sub: Subscription = new Subscription();
	private navigation$!: Observable<void>;

	constructor(private router: Router,
				private infomessageService: InfoMessageService,
				private importConfigFacade: ImportConfigFacade,
				private roleService: RoleService,
				private orderFacade: OrderFacade) {
		this.initNavigationItems();
		// Subscribe once to trigger initial load and seed BehaviorSubjects
		this.sub.add(this.navigation$.subscribe());
		this.initRouteListener();
	}

	ngOnDestroy(): void {
		this.sub.unsubscribe();
	}

	getMainNavigationItems(): Observable<NavigationConfigModel[]> {
		return this.mainNavigation.asObservable();
	}

	getSubNavigationObservable(): Observable<NavigationConfigModel[] | undefined> {
		return this.activeSubNavigation.asObservable();
	}

	getBreadcrumbsObservable(): Observable<Breadcrumb[]> {
		return this.breadcrumbs.asObservable();
	}

	private initRouteListener(): void {
		this.sub.add(
			this.router.events.pipe(
				// tslint:disable-next-line:typedef
				filter((routerEvent): routerEvent is NavigationEnd => {
					return routerEvent instanceof NavigationEnd;
				})
			).subscribe((navEvent: NavigationEnd) => {
				this.customBreadcrumbTitle.next(null);
				this.activeSubNavigation.next(this.getSubNavigationItems(navEvent));
				this.breadcrumbs.next(this.getBreadCrumbs(navEvent));
			})
		);
	}

	private getSubNavigationItems(navEvent: NavigationEnd): NavigationConfigModel[] {
		if (!this.navConfig) {
			return [];
		}
		const segments = this.getPathSegments(navEvent);
		const childValues = this.findEntry(segments[0], this.navConfig)?.children;

		return childValues || [];

	}


	private getBreadCrumbs(navEvent: NavigationEnd): Breadcrumb[] {
		if (!this.navConfig) {
			return [];
		}
		const segments = this.getPathSegments(navEvent);

		const breadcrumbLinks: Breadcrumb[] = [];

		// Use list of path-segments to build the bread-crumb-list
		// For each segment go one navigation-layer deeper to get the next child of the nav-config data
		let previousElement: NavigationConfigModel[] = this.navConfig;
		segments.forEach((segment: string, index: number) => {
			try {
				const data = this.findEntry(segment, previousElement);
				breadcrumbLinks.push({
					link: data.link,
					title: data.title,
					active: false
				});
				if (data.children) {
					previousElement = data.children;
				}
			} catch (e) {
				// If we can't find an entry, it might be a dynamic segment (like :orderId)
				// We still want a breadcrumb for it
				const isLast = index === segments.length - 1;
				const title = isLast && this.customBreadcrumbTitle.value ? this.customBreadcrumbTitle.value : segment;
				breadcrumbLinks.push({
					link: '/' + segments.slice(0, index + 1).join('/'),
					title: title,
					active: false
				});
			}
		});
		// Set last entry to active - RouterlinkActive considers all routes including parents as active if a child is active
		if (breadcrumbLinks.length > 0) {
			breadcrumbLinks[breadcrumbLinks.length - 1].active = true;
		}
		return breadcrumbLinks;
	}

	setBreadcrumbTitle(title: string): void {
		this.customBreadcrumbTitle.next(title);
		// Trigger breadcrumb update
		const currentCrumbs = this.breadcrumbs.value;
		if (currentCrumbs.length > 0) {
			currentCrumbs[currentCrumbs.length - 1].title = title;
			this.breadcrumbs.next([...currentCrumbs]);
		}
	}

	private getPathSegments(navEvent: NavigationEnd): string[] {
		// urlAfterRedirect because on first entry the user is on "/" and gets redirected
		let url = navEvent.urlAfterRedirects;

		if (url.startsWith('/')) {
			url = url.substr(1);
		}

		const segments = url.split('/');

		if (segments.length === 0) {
			throw Error(`no route-segment found: ${url}`);
		}
		return segments.map((segment: string) => {
			return this.sanitizeSegment(segment);
		});
	}

	private sanitizeSegment(segment: string): string {
		// Clean segments from #state stuff, that keycloak adds after the redirect
		const index = segment.indexOf('#');

		if (index >= 0) {
			return segment.substr(index);
		}
		// Remove query-params for segment-matching
		const queryParamIndex = segment.indexOf('?');

		if (queryParamIndex >= 0) {
			return segment.substr(0, queryParamIndex);
		}

		return segment;
	}


	private initNavigationItems(): void {
		this.navigation$ = forkJoin([
			of(null), // comment next line in, if sales is reactivated
			// this.getSalesNavigation(),
			this.getStaticNavigation()
		]).pipe(
			map(([salesNavigation, staticNavigation]: [NavigationConfigModel | null, NavigationConfigModel[]]) => {
				if (salesNavigation) {
					staticNavigation.push(salesNavigation);
				}

				staticNavigation.sort(this.sortBySortOrder);

				this.navConfig = this.filterByRoles(staticNavigation);
				this.mainNavigation.next(this.navConfig);
				// Just to redirect the user, to a working page.
				// Default routing is on sales (see app-routing.module.ts)
				// if (!salesNavigation) {
				// 	this.router.navigateByUrl('/products');
				// }
			}),
			// Important! Otherwise this obs is re-triggered all the time
			shareReplay()
		);
	}

	private filterByRoles(items: NavigationConfigModel[]): NavigationConfigModel[] {
		return items.filter((item: NavigationConfigModel) => {
			return this.roleService.hasAnyRole(item.roles);
		}).map((item: NavigationConfigModel) => {
			return item.children ? {...item, children: this.filterByRoles(item.children)} : item;
		});
	}

	private getStaticNavigation(): Observable<NavigationConfigModel[]> {
		return this.importConfigFacade.hasEmailImporter().pipe(
			// Der Check hängt an einem Admin-Endpoint — ein normaler Nutzer bekommt
			// dort 403. Ohne Fallback bliebe die Navigation (und damit jede Seite) leer.
			catchError(() => of(this.roleService.isAdmin() ? false : true)),
			map((hasEmailImporter: boolean) => {
				const productSubNav = this.createProductSubNav();
				let productMainLink = this.roleService.isAdmin() ? '/products' : '/orders';

				if (hasEmailImporter) {
					productSubNav.unshift({
						title: 'Einträge',
						link: '/orders',
						icon: 'fas fa-clipboard-check',
						roles: ORDER_ROLES,
					});
					productMainLink = '/orders';
				}

				return [{
					title: 'Bestellungen',
					link: productMainLink,
					icon: 'fas fa-layer-group',
					children: productSubNav,
					sortOrder: 1,
					roles: ORDER_ROLES,
				} as NavigationConfigModel, {
					title: 'Admin-Bereich',
					link: '/admin',
					icon: 'fas fa-key',
					children: this.createAdminSubNav(),
					sortOrder: 3,
					roles: ADMIN_ROLES,
				} as NavigationConfigModel];
			})
		);
	}

	private getSalesNavigation(): Observable<NavigationConfigModel | null> {
		return this.orderFacade.getWorkflowStatusWithSalesChannels().pipe(
			map((channelStatusMap: SalesChannelWithWorkflowStatus) => {
				// Create sub-nav-items for every sales-channel and every status
				const salesChannelGroups = Object.entries(channelStatusMap).map((entry: [string, OrderStatus[]]) => {
					const channel: SalesChannel = entry[0] as SalesChannel; // Cast to SalesChannel, because Object.entries() returns string for the key
					const statusList: OrderStatus[] = entry[1];
					return this.createSubNavForSalesChannelAndOrderStatus(channel, statusList);
				});

				salesChannelGroups.sort(this.sortBySortOrder);

				// Create main-nav item with the newly created sales-channel-status nav
				return {
					title: 'Sales-Bereich',
					link: '/sales',
					icon: 'fas fa-shopping-basket',
					children: salesChannelGroups,
					sortOrder: 2,
				} as NavigationConfigModel;
			}),
			catchError((err: Error) => {
				console.error(err);
				this.infomessageService.createMessage('Sales-Bereich konnte nicht geladen werden', 'Fehler', InfoMessageSeverity.warning);
				// If something goes wrong (e.g. Order-Service not reachable), return null and let the caller handle it
				return of(null);
			})
		);
	}

	private createSubNavForSalesChannelAndOrderStatus(channel: SalesChannel, statusList: OrderStatus[]): NavigationConfigModel {
		const channelGroup: NavigationConfigModel = {
			title: channel,
			link: this.getSalesChannelNavigationLink(channel),
			icon: 'fas fa-store',
			sortOrder: this.getSortOrderForSalesChannel(channel)
		};

		// Collect all in map - some order-status lead to the same nav-link
		const distinctEntries: Map<NavigationPath, NavigationConfigModel> = new Map();
		// Create nav items for each order-status
		statusList.forEach((orderStatus: OrderStatus) => {
			// Get navigation-path for order-status ('/new' e.g.)
			const navPath = OrderStatusNavigationPath.get(orderStatus);
			if (!navPath) {
				throw Error(`No navigation-path found for '${orderStatus}'`);
			}
			// Some order-status lead to the same navigation-item -> check if already created
			if (distinctEntries.has(navPath)) {
				return;
			}

			// Now get path-config for the navigation-path
			const navConfig = OrderStatusNavigationPathConfig.get(navPath);
			if (!navConfig) {
				throw Error(`No navigation-path found for '${orderStatus}'`);
			}

			distinctEntries.set(navPath, {
				title: navConfig.navigationTitle,
				link: this.getOrderStatusNavigationLink(channel as SalesChannel, orderStatus),
				icon: '', // Sub-Nav items don't have icons,
				sortOrder: navConfig.sortOrder,
			});
		});

		// Add the non-status nav-items
		const reviewConfig = OrderStatusNavigationPathConfig.get(NavigationPath.inReview);
		if (!reviewConfig) {
			throw Error(`No navigation-path found for '${NavigationPath.inReview}'`);
		}

		distinctEntries.set(NavigationPath.inReview, {
			title: reviewConfig.navigationTitle,
			link: this.getSalesChannelNavigationLink(channel as SalesChannel) + '/review',
			icon: '',
			sortOrder: reviewConfig.sortOrder,
		});

		const allConfig = OrderStatusNavigationPathConfig.get(NavigationPath.all);
		if (!allConfig) {
			throw Error(`No navigation-path found for '${NavigationPath.all}'`);
		}

		distinctEntries.set(NavigationPath.all, {
			title: allConfig.navigationTitle,
			link: this.getSalesChannelNavigationLink(channel as SalesChannel) + '/all',
			icon: '',
			sortOrder: allConfig.sortOrder,
		});

		// Sort it by sort-order!
		const navItems = Array.from(distinctEntries.values());
		navItems.sort(this.sortBySortOrder);
		channelGroup.children = navItems;
		return channelGroup;
	}

	private createProductSubNav(): NavigationConfigModel[] {
		const children: NavigationConfigModel[] = [];
		//children.push({
		//		title: 'Produkte',
		//		link: '/products/all',
		//		icon: 'fas fa-layer-group',
		//	}
		//);
		//children.push({
		//		title: 'Festpreise',
		//		link: '/products/fixedPrices',
		//		icon: 'fas fa-comment-dollar',
		//	}
		//);
		//children.push({
		//		title: 'Preisübersicht',
		//		link: '/products/priceOverview',
		//		icon: 'fas fa-money-bill-wave',
		//	}
		//);
		//children.push({
		//		title: 'Lieferanten Lager',
		//		link: '/products/supplierStock',
		//		icon: 'fas fa-gifts',
		//	}
		//);
		//children.push({
		//		title: 'Bulk-Anlage',
		//		link: '/products/bulkCreate',
		//		icon: 'fas fa-upload',
		//	}
		//);
		//children.push({
		//		title: 'Gesperrt',
		//		link: '/products/locked',
		//		icon: 'fas fa-lock',
		//	}
		//);

		return children;
	}

	private createAdminSubNav(): NavigationConfigModel[] {
		const children = [];
		children.push({
				title: 'Feld-Management',
				link: '/admin/fields',
				icon: 'fas fa-stream'
			}
		);
		children.push({
				title: 'Labels',
				link: '/admin/labels',
				icon: 'fas fa-tag',
			}
		);
		children.push({
				title: 'Auslieferung',
				link: '/admin/delivery',
				icon: 'fas fa-truck-fast',
			}
		);
		//children.push({
		//		title: 'Stammdaten',
		//		link: '/admin/master-data',
		//		icon: 'fas fa-file-alt',
		//	}
		//);
		//children.push({
		//		title: 'Zahlungsoptionen',
		//		link: '/admin/payment-methods',
		//		icon: 'fab fa-paypal',
		//	}
		//);
		//children.push({
		//		title: 'Margenregeln',
		//		link: '/admin/margin-rules',
		//		icon: 'fas fa-percent',
		//	}
		//);
		//children.push({
		//		title: 'Lieferanten',
		//		link: '/admin/suppliers',
		//		icon: 'fas fa-truck-loading',
		//	}
		//);
		//children.push({
		//		title: 'Marktplätze',
		//		link: '/admin/marketplaces',
		//		icon: 'fas fa-store',
		//	}
		//);
		//children.push({
		//		title: 'Verkaufskataloge',
		//		link: '/admin/catalog',
		//		icon: 'fas fa-cubes',
		//	}
		//);
		//children.push({
		//		title: 'Kategoriebaum',
		//		link: '/admin/category-tree',
		//		icon: 'fas fa-sitemap',
		//	}
		//);
		children.push({
				title: 'Importer',
				link: '/admin/importer',
				icon: 'fas fa-exchange-alt',
			}
		);
		children.push({
				title: 'Exporter',
				link: '/admin/exporter',
				icon: 'fas fa-exchange-alt',
			}
		);
		//children.push({
		//		title: 'Bestellungen',
		//		link: '/admin/orders',
		//		icon: 'fas fa-shopping-cart',
		//	}
		//);
		return children;
	}

	private getOrderStatusNavigationLink(salesChannel: SalesChannel, orderStatus: OrderStatus): string {
		const pathForOrderStatus = OrderStatusNavigationPath.get(orderStatus);
		if (!pathForOrderStatus) {
			throw Error(`No routing-path for order-status '${orderStatus} found'`);
		}
		return [this.getSalesChannelNavigationLink(salesChannel), pathForOrderStatus].join('/');
	}

	private getSalesChannelNavigationLink(salesChannel: SalesChannel): string {

		const pathElements: string[] = [
			'sales',
		];
		const channel = SalesChannelNavigationPath.get(salesChannel);
		if (!channel) {
			throw Error(`No routing-path for saleschannel '${salesChannel}' found`);
		}
		pathElements.push(channel);
		return '/' + pathElements.join('/');
	}

	private findEntry(segment: string, navList: NavigationConfigModel[]): NavigationConfigModel {
		const navModel = navList.find((entry: NavigationConfigModel) => {
			return entry.link.includes(segment);
		});
		if (!navModel) {
			throw Error(`No nav-config found for path-segment ${segment}`);
		}
		return navModel;
	}

	getNavigation(): Observable<void> {
		return this.navigation$;
	}

	private getSortOrderForSalesChannel(channel: SalesChannel): number {
		switch (channel) {
			case SalesChannel.Egis:
				return 1;
			case SalesChannel.Octo:
				return 2;
			case SalesChannel.Conrad:
				return 3;
			case SalesChannel.ItScope:
				return 4;
			case SalesChannel.ItScopeB2B:
				return 5;
			case SalesChannel.Nagarro:
				return 6;
			case SalesChannel.Asb:
				return 7;
			case SalesChannel.Wucato:
				return 8;
		}
	}

	private sortBySortOrder(a: NavigationConfigModel, b: NavigationConfigModel): number {
		// tslint:disable-next-line:no-non-null-assertion
		return a.sortOrder! - b.sortOrder!;
	}
}

export const OrderStatusNavigationPathConfig = new Map<NavigationPath, NavigationConfig>([
	[NavigationPath.new, {navigationTitle: 'Neu', sortOrder: 1}],
	[NavigationPath.paid, {navigationTitle: 'Bezahlt', sortOrder: 2}],
	[NavigationPath.inProgress, {navigationTitle: 'In Bearbeitung', sortOrder: 3}],
	[NavigationPath.shipped, {navigationTitle: 'Versendet', sortOrder: 4}],
	[NavigationPath.invoiced, {navigationTitle: 'Fakturiert', sortOrder: 5}],
	[NavigationPath.inReview, {navigationTitle: 'In Klärung!', sortOrder: 6}],
	[NavigationPath.cancelled, {navigationTitle: 'Storniert', sortOrder: 7}],
	[NavigationPath.all, {navigationTitle: 'Alle', sortOrder: 8}],
]);

interface NavigationConfig {
	navigationTitle: string;
	sortOrder: number;
}
